import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import {
  SignalCheckbox, SignalFormDirective,
  SignalNumberField,
  SignalTextField,
} from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';

interface RangeSliderConfigModel {
  label: string;
  min: number;
  max: number;
  step: number;
  defaultStart: number;
  defaultEnd: number;
  required: boolean;
  helpText: string;
}

@Component({
  selector: 'app-range-slider-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalNumberField,
    SignalCheckbox,
    SignalFormDirective,
  ],
  templateUrl: './range-slider-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeSliderConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();
  readonly model = signal<RangeSliderConfigModel>({
    label: '',
    helpText: '',
    min: 0,
    max: 100,
    step: 1,
    defaultStart: 0,
    defaultEnd: 100,
    required: false,
  });
  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
    required(schema.min);
    required(schema.max);
    required(schema.step);
  });
  private lastFieldId: string | null = null;
  private suppressEmit = false;
  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        min: next.sliderMin ?? 0,
        max: next.sliderMax ?? 100,
        step: next.sliderStep ?? 1,
        defaultStart: next.sliderDefaultStart ?? 0,
        defaultEnd: next.sliderDefaultEnd ?? 100,
        required: next.required ?? validation.required ?? false,
        helpText: next.helpText ?? '',
      });
    });
    effect(() => {
      const draft = this.model();

      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim() || field.label,
        helpText: draft.helpText.trim() || undefined,
        sliderMin: draft.min,
        sliderMax: draft.max,
        sliderStep: draft.step,
        required: draft.required,
        sliderDefaultStart: draft.defaultStart,
        sliderDefaultEnd: draft.defaultEnd,
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }
}
